import { useAccount, useConnect, useDisconnect, useBalance, useChainId, useSwitchChain, useSignMessage, useSignTypedData, useSendTransaction, useConnectors, useEnsName, useEnsAvatar } from 'wagmi';
import { useCoinleyWallet } from './provider';
import { formatEther } from 'viem';
import { useCallback, useEffect, useState } from 'react';
// Wallet detection utility
function detectInstalledWallets() {
    const wallets = [];
    if (typeof window === 'undefined')
        return wallets;
    const ethereum = window.ethereum;
    if (ethereum) {
        // MetaMask
        if (ethereum.isMetaMask) {
            wallets.push({ name: 'MetaMask', icon: '🦊', provider: 'MetaMask', id: 'metaMask' });
        }
        // Coinbase Wallet
        if (ethereum.isCoinbaseWallet || ethereum.selectedProvider?.isCoinbaseWallet) {
            wallets.push({ name: 'Coinbase Wallet', icon: '🔵', provider: 'Coinbase', id: 'coinbaseWallet' });
        }
        // Trust Wallet
        if (ethereum.isTrust) {
            wallets.push({ name: 'Trust Wallet', icon: '🛡️', provider: 'Trust', id: 'trust' });
        }
        // OKX Wallet
        if (ethereum.isOKExWallet) {
            wallets.push({ name: 'OKX Wallet', icon: '⚫', provider: 'OKX', id: 'okx' });
        }
        // Rabby Wallet
        if (ethereum.isRabby) {
            wallets.push({ name: 'Rabby Wallet', icon: '🐰', provider: 'Rabby', id: 'rabby' });
        }
        // Brave Wallet
        if (ethereum.isBraveWallet) {
            wallets.push({ name: 'Brave Wallet', icon: '🦁', provider: 'Brave', id: 'brave' });
        }
        // Rainbow Wallet
        if (ethereum.isRainbow) {
            wallets.push({ name: 'Rainbow Wallet', icon: '🌈', provider: 'Rainbow', id: 'rainbow' });
        }
        // Zerion Wallet
        if (ethereum.isZerion) {
            wallets.push({ name: 'Zerion Wallet', icon: '🔷', provider: 'Zerion', id: 'zerion' });
        }
        // TokenPocket
        if (ethereum.isTokenPocket) {
            wallets.push({ name: 'TokenPocket', icon: '💼', provider: 'TokenPocket', id: 'tokenpocket' });
        }
        // imToken
        if (ethereum.isImToken) {
            wallets.push({ name: 'imToken', icon: '💎', provider: 'imToken', id: 'imtoken' });
        }
        // Check multiple providers array
        if (ethereum.providers && Array.isArray(ethereum.providers)) {
            ethereum.providers.forEach((provider) => {
                if (provider.isMetaMask && !wallets.find(w => w.id === 'metaMask')) {
                    wallets.push({ name: 'MetaMask', icon: '🦊', provider: 'MetaMask', id: 'metaMask' });
                }
                if (provider.isCoinbaseWallet && !wallets.find(w => w.id === 'coinbaseWallet')) {
                    wallets.push({ name: 'Coinbase Wallet', icon: '🔵', provider: 'Coinbase', id: 'coinbaseWallet' });
                }
                if (provider.isTrust && !wallets.find(w => w.id === 'trust')) {
                    wallets.push({ name: 'Trust Wallet', icon: '🛡️', provider: 'Trust', id: 'trust' });
                }
                if (provider.isOKExWallet && !wallets.find(w => w.id === 'okx')) {
                    wallets.push({ name: 'OKX Wallet', icon: '⚫', provider: 'OKX', id: 'okx' });
                }
                if (provider.isRabby && !wallets.find(w => w.id === 'rabby')) {
                    wallets.push({ name: 'Rabby Wallet', icon: '🐰', provider: 'Rabby', id: 'rabby' });
                }
                if (provider.isZerion && !wallets.find(w => w.id === 'zerion')) {
                    wallets.push({ name: 'Zerion Wallet', icon: '🔷', provider: 'Zerion', id: 'zerion' });
                }
                if (provider.isRainbow && !wallets.find(w => w.id === 'rainbow')) {
                    wallets.push({ name: 'Rainbow Wallet', icon: '🌈', provider: 'Rainbow', id: 'rainbow' });
                }
            });
        }
        // Generic fallback if none detected
        if (wallets.length === 0) {
            wallets.push({ name: 'Browser Wallet', icon: '🌐', provider: 'Generic', id: 'injected' });
        }
    }
    // Check for Keplr (Cosmos)
    if (window.keplr) {
        wallets.push({ name: 'Keplr', icon: '🌌', provider: 'Keplr', id: 'keplr' });
    }
    // Check for Phantom (Solana)
    if (window.phantom?.solana) {
        wallets.push({ name: 'Phantom', icon: '👻', provider: 'Phantom', id: 'phantom' });
    }
    return wallets;
}
// Hook for wallet detection - built into the library
export function useWalletDetection() {
    const [detectedWallets, setDetectedWallets] = useState([]);
    useEffect(() => {
        const detect = () => {
            const wallets = detectInstalledWallets();
            setDetectedWallets(wallets);
        };
        // Detect immediately and after delay (some wallets inject later)
        detect();
        const timeout = setTimeout(detect, 1000);
        return () => clearTimeout(timeout);
    }, []);
    return {
        detectedWallets,
        hasWallets: detectedWallets.length > 0,
        walletCount: detectedWallets.length
    };
}
// Main hook that abstracts wagmi complexity
export function useWallet() {
    const { address, isConnected, isConnecting, isDisconnected } = useAccount();
    const chainId = useChainId();
    const { disconnect } = useDisconnect();
    const { closeModal } = useCoinleyWallet();
    const connectors = useConnectors();
    const { data: balance, isLoading: isBalanceLoading } = useBalance({
        address,
        query: {
            enabled: !!address
        }
    });
    // ENS resolution
    const { data: ensName } = useEnsName({
        address,
        chainId: 1, // ENS is on mainnet
        query: {
            enabled: !!address
        }
    });
    const { data: ensAvatar } = useEnsAvatar({
        name: ensName || undefined,
        chainId: 1,
        query: {
            enabled: !!ensName
        }
    });
    // Simplified disconnect that also closes modal
    const handleDisconnect = useCallback(() => {
        disconnect();
        closeModal();
    }, [disconnect, closeModal]);
    // Format balance as string
    const formattedBalance = balance ? formatEther(balance.value) : '0';
    return {
        // Wallet state
        address,
        isConnected,
        isConnecting,
        isDisconnected,
        chainId,
        // ENS
        ensName,
        ensAvatar,
        // Balance
        balance: balance?.value.toString() || '0',
        balanceFormatted: formattedBalance,
        balanceSymbol: balance?.symbol || 'ETH',
        isBalanceLoading,
        // Actions
        disconnect: handleDisconnect,
        // Available connectors
        connectors: connectors.map(connector => ({
            id: connector.id,
            name: connector.name,
            icon: connector.icon,
            installed: connector.installed,
            ready: connector.ready
        }))
    };
}
// Hook for connecting to specific wallet
export function useWalletConnect() {
    const { connect, connectors, isPending, error } = useConnect();
    const { closeModal } = useCoinleyWallet();
    const connectWallet = useCallback((connectorId) => {
        const connector = connectors.find((c) => c.id === connectorId);
        if (connector) {
            // Add timeout handling for stuck connections
            const connectionTimeout = setTimeout(() => {
                console.warn(`Connection to ${connector.name} timed out after 30 seconds`);
            }, 30000);
            connect({ connector }, {
                onSuccess: () => {
                    clearTimeout(connectionTimeout);
                    closeModal();
                },
                onError: (error) => {
                    clearTimeout(connectionTimeout);
                    console.error(`Failed to connect to ${connector.name}:`, error);
                }
            });
        }
        else {
            console.error(`Connector ${connectorId} not found`);
        }
    }, [connect, connectors, closeModal]);
    return {
        connectWallet,
        isPending,
        error,
        connectors: connectors.map((connector) => ({
            id: connector.id,
            name: connector.name,
            icon: connector.icon,
            installed: connector.installed ?? true, // Default to true if undefined
            ready: connector.ready ?? true // Default to true if undefined
        }))
    };
}
/**
 * Hook for switching networks/chains
 * @returns Object with async switchChain function, loading state, and error
 * @example
 * const { switchChain, isSwitching, error } = useNetworkSwitch()
 * await switchChain({ chainId: 137 }) // Switch to Polygon
 */
export function useNetworkSwitch() {
    const { switchChainAsync, isPending, error } = useSwitchChain();
    const switchChain = useCallback(async (params) => {
        try {
            await switchChainAsync(params);
        }
        catch (error) {
            console.error('Switch chain error:', error);
            throw error;
        }
    }, [switchChainAsync]);
    return {
        switchChain,
        isSwitching: isPending,
        error
    };
}
/**
 * Hook for signing messages
 * @returns Object with async sign function, loading state, and error
 * @example
 * const { sign, isSigning, error } = useWalletSign()
 * const signature = await sign('Hello World!')
 */
export function useWalletSign() {
    const { signMessageAsync, isPending, error } = useSignMessage();
    const sign = useCallback(async (message) => {
        try {
            const signature = await signMessageAsync({ message });
            return signature;
        }
        catch (error) {
            console.error('Sign message error:', error);
            throw error;
        }
    }, [signMessageAsync]);
    return {
        sign,
        isSigning: isPending,
        error
    };
}
/**
 * Hook for signing EIP-712 typed data
 * @returns Object with async signTypedData function, loading state, and error
 * @example
 * const { signTypedData, isSigning, error } = useWalletSignTypedData()
 * const signature = await signTypedData({
 *   domain: { name: 'MyApp', version: '1', chainId: 1 },
 *   types: { Person: [{ name: 'name', type: 'string' }] },
 *   primaryType: 'Person',
 *   message: { name: 'Alice' }
 * })
 */
export function useWalletSignTypedData() {
    const { signTypedDataAsync, isPending, error } = useSignTypedData();
    const signTypedMessage = useCallback(async (typedData) => {
        try {
            const signature = await signTypedDataAsync(typedData);
            return signature;
        }
        catch (error) {
            console.error('Sign typed data error:', error);
            throw error;
        }
    }, [signTypedDataAsync]);
    return {
        signTypedData: signTypedMessage,
        isSigning: isPending,
        error
    };
}
/**
 * Hook for sending transactions
 * @returns Object with async sendTransaction function, loading state, and error
 * @example
 * const { sendTransaction, isSending, error } = useWalletTransaction()
 * const hash = await sendTransaction({
 *   to: '0x...',
 *   value: '0x5AF3107A4000', // 0.0001 ETH
 *   data: '0x...'
 * })
 */
export function useWalletTransaction() {
    const { sendTransactionAsync, isPending, error } = useSendTransaction();
    const sendTransaction = useCallback(async (transaction) => {
        try {
            const hash = await sendTransactionAsync(transaction);
            return hash;
        }
        catch (error) {
            console.error('Send transaction error:', error);
            throw error;
        }
    }, [sendTransactionAsync]);
    return {
        sendTransaction,
        isSending: isPending,
        error
    };
}
// Hook for token balance
export function useTokenBalance(tokenAddress) {
    const { address } = useAccount();
    const { data: balance, isLoading } = useBalance({
        address,
        token: tokenAddress,
        query: {
            enabled: !!address && !!tokenAddress
        }
    });
    return {
        balance: balance?.value.toString() || '0',
        balanceFormatted: balance ? formatEther(balance.value) : '0',
        symbol: balance?.symbol,
        decimals: balance?.decimals,
        isLoading
    };
}
// Hook for modal control
export function useWalletModal() {
    const { isModalOpen, openModal, closeModal } = useCoinleyWallet();
    return {
        isOpen: isModalOpen,
        openModal,
        closeModal
    };
}
//# sourceMappingURL=hooks.js.map